import { useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import {
  csvExporter,
  handleFilterChange,
  xlsxExporter,
} from "../../../../helpers";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ManageOrderDrawer from "../../../../components/sections/front/Order/Drawer/ManageOrderDrawer";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import {
  exportReports,
  orderReportList,
} from "../../../../services/eventReportService";
import ExportDropdown from "../EventReport/ExportDropdown";
import Button from "../../../form/Button";
import DatePicker from "../../../form/DatePicker";
import { countList } from "../../../../services/eventService";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../../helpers/classNames";

// Payment status options for filtering
const paymentStatusList = [
  { name: "pending", value: "pending", type: "checkbox" },
  { name: "Success", value: "success", type: "checkbox" },
  { name: "failed", value: "failed", type: "checkbox" },
];

const statusList = [
  { name: "processing", value: "processing", type: "checkbox" },
  // { name: "shipped", value: "shipped", type: "checkbox" },
  // { name: "pickup", value: "pickup", type: "checkbox" },
  // { name: "delivered", value: "delivered", type: "checkbox" },
  { name: "canceled", value: "canceled", type: "checkbox" },
  { name: "confirmed", value: "confirmed", type: "checkbox" },
  // { name: "return-request", value: "return-request", type: "checkbox" },
  { name: "cancel-request", value: "cancel-request", type: "checkbox" },
  { name: "partial-refund", value: "partial-refund", type: "checkbox" },
  // { name: "rejected", value: "rejected", type: "checkbox" },
];

const getStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const statusStyles = {
    processing: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    shipped: {
      backgroundColor: "#DBEAFE", // Blue background
      color: "#1D4ED8", // Dark blue text
      borderColor: "#3B82F6", // Blue border
    },
    pickup: {
      backgroundColor: "#FCE7F3", // Pink background
      color: "#BE185D", // Dark pink text
      borderColor: "#EC4899", // Pink border
    },
    delivered: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    confirmed: {
      backgroundColor: "#ECFDF5", // Light emerald background - Fresh confirmation color
      color: "#065F46", // Dark emerald text - Strong contrast
      borderColor: "#059669", // Emerald border - Professional accent
    },
    canceled: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
    "return-request": {
      backgroundColor: "#FDF4FF", // Purple background
      color: "#7C2D92", // Dark purple text
      borderColor: "#A855F7", // Purple border
    },
    rejected: {
      backgroundColor: "#F3F4F6", // Gray background
      color: "#374151", // Dark gray text
      borderColor: "#6B7280", // Gray border
    },
  };

  return (
    statusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

// Payment Status Styling Function
const getPaymentStatusStyle = (status) => {
  const statusLower = status?.toLowerCase();

  const paymentStatusStyles = {
    pending: {
      backgroundColor: "#FEF3C7", // Yellow background
      color: "#D97706", // Dark yellow text
      borderColor: "#F59E0B", // Yellow border
    },
    success: {
      backgroundColor: "#D1FAE5", // Green background
      color: "#047857", // Dark green text
      borderColor: "#10B981", // Green border
    },
    failed: {
      backgroundColor: "#FEE2E2", // Red background
      color: "#DC2626", // Dark red text
      borderColor: "#EF4444", // Red border
    },
  };

  return (
    paymentStatusStyles[statusLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const OrderList = ({ selectedEvents = [], setDrawer = () => {} }) => {
  const { event } = useOutletContext();
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Fetch orders data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "order-reports",
      filters,
      search,
      selectedEvents?.map((item) => item?._id),
    ],
    queryFn: async () => {
      const res = await orderReportList({
        ...filters,
        ...(filters?.startDate &&
          filters?.endDate && {
            startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
          }),
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        paymentStatus: "success",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    // enabled: selectedEvents?.length > 0,
  });

  // Handle export functionality with client-side download and API call
  const tableHeadData = [
    {
      _id: "OrderId",
      name: t("Order Id"),
      align: "left",
    },
    {
      _id: 1,
      name: t("Total Amount"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Event Name"),
      align: "left",
    },
    {
      _id: "tickets",
      name: t("Tickets"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Order by"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Order date"),
      align: "left",
    },
    {
      _id: "paymentStatus",
      name: t("payment Status"),
      align: "left",
      // ...tableFilter({
      //   filterKey: "paymentStatus",
      //   filters,
      //   setFilters: (val) => {
      //     setFilters((pre) => ({ ...pre, ...val, page: 1 }));
      //   },
      //   filterList: [...paymentStatusList],
      // }),
    },
    {
      _id: 5,
      name: t("Order Status"),
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...statusList],
      }),
    },
  ];

  // Table Body Data
  const tableData = data?.data?.map((item) => {
    const statusText =
      item?.status?.info?.charAt(0)?.toUpperCase() +
        item?.status?.info?.slice(1) || "-";
    const statusStyle = getStatusStyle(statusText);

    // Payment status styling
    const paymentStatus = item?.payment?.status || "pending";
    const paymentStatusText =
      paymentStatus.charAt(0)?.toUpperCase() + paymentStatus.slice(1);
    const paymentStatusStyle = getPaymentStatusStyle(paymentStatus);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] font-medium",
          data: `${
            item?.eventData?.currency?.symbol || "$"
          } ${item?.total?.toString()}`,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: "tickets",
          type: "text",
          align: "left",
          cellClass: "!max-w-[70px] text-purple-600",
          data: item?.ticketCount || 0,
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600",
          data:
            item?.userData?.firstName || item?.userData?.lastName
              ? `${item?.userData?.firstName} ${item?.userData?.lastName}`
              : "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-gray-600",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY hh:mm A") || "-",
        },
        {
          _id: "paymentStatus",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: paymentStatusStyle.backgroundColor,
                color: paymentStatusStyle.color,
                borderColor: paymentStatusStyle.borderColor,
              }}
            >
              {paymentStatusText}
            </span>
          ),
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
              style={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                borderColor: statusStyle.borderColor,
              }}
            >
              {statusText}
            </span>
          ),
        },
      ],
    };
  });

  // Page manage response
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(
            ["order", filters, search, selectedOrganization],
            (state) => {
              if (state) {
                let stateData = JSON.parse(JSON.stringify(state?.data));
                stateData = [...stateData, res?.data];
                return { ...state, data: stateData };
              }
            }
          );
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["order", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(
          ["order", filters, search, selectedOrganization],
          (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = stateData?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state, data: stateData };
            }
          }
        );
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries([
            "order",
            filters,
            search,
            selectedOrganization,
          ]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      toast.info("Preparing export data...");

      const exportResponse = await orderReportList({
        ...filters,
        event: selectedEvents?.map((item) => item?._id),
        paymentStatus: "success",
        limit: data?.metadata?.totalDocs,
        page: 1,
      });

      if (exportResponse?.docs?.status !== 200) {
        toast.error(exportResponse?.message || "Failed to fetch export data");
        return;
      }

      const saleData = exportResponse?.docs?.data || [];

      if (!saleData || saleData.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Transform the sale data for export
      const exportData = saleData.map((sale, i) => ({
        "Order Id": sale?.orderId,
        "Total Amount": "$" + sale?.total?.toString() || "-",
        "Event Name": lngDetect(sale?.eventData?.title) || "-",
        "Order by":
          sale?.userData?.firstName || sale?.userData?.lastName
            ? `${sale?.userData?.firstName} ${sale?.userData?.lastName}`
            : "-",
        "Order date": dayjs(sale?.createdAt).format("MMM DD, YYYY hh:mm A"),
        "payment Status": sale?.payment?.status || "pending",
        "Order Status":
          sale?.status?.info?.charAt(0)?.toUpperCase() +
            sale?.status?.info?.slice(1) || "-",
      }));

      if (format === "csv") {
        csvExporter(exportData, "orders").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      } else if (format === "xlsx") {
        xlsxExporter(exportData, "orders").then(async (result) => {
          if (result?.status === 200) {
            const formData = new FormData();
            formData.append("file", result?.file, result?.fileName);
            formData.append("type", "orders");
            formData.append("name", result?.fileName);
            formData.append("organization", selectedOrganization);
            formData.append("status", "success");
            const resp = await exportReports(formData);
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
      setIsExportOpen(false);
    }
  };

  const { data: eventCount, isLoading: eventCountLoading } = useQuery({
    queryKey: [
      "event-count-data",
      selectedEvents?.map((item) => item?._id),
      filters?.startDate &&
        filters?.endDate && {
          startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
        },
    ],
    queryFn: async () => {
      const res = await countList({
        ...(filters?.startDate &&
          filters?.endDate && {
            startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
          }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        sortQuery: "-createdAt",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const statistics = [
    { label: t("Ticket Sold"), value: eventCount?.ticketsSold || 0 },
    { label: t("Orders"), value: eventCount?.totalOrder || 0 },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-5 absolute -top-28 right-6">
        {/* <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-gray-600 text-sm font-medium mb-2">
            {t("Net Sales")}
          </h3>
          <div className="text-3xl font-bold text-gray-900">
            $ {eventCount?.netSales || 0}
          </div>
        </div> */}

        {statistics?.map((s, i) => (
          <div
            key={i?.toString()}
            className={classNames(
              "relative pl-4",
              "before:content-[''] before:w-[6px] before:h-full before:bg-green-300 before:rounded-full before:absolute before:top-0 before:left-0"
            )}
          >
            <div className="text-sm text-gray-400 font-medium">{s?.label}</div>
            <div className="text-lg font-semibold text-deep-purple">
              {s?.value}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        {/* Search and Controls */}
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder={t("Search here")}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            <div className="w-full md:w-60">
              <DatePicker
                dateFormat="DD MMM YYYY"
                range
                onChange={(val) => {
                  if (val?.startDate && val?.endDate) {
                    setFilters((pre) => ({ ...pre, ...val }));
                  } else {
                    setFilters((pre) => {
                      delete pre?.startDate;
                      delete pre?.endDate;
                      return { ...pre };
                    });
                  }
                }}
                inputClassName="rounded-full h-12 px-4"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4 items-center w-full md:w-auto">
            <Button
              buttonIcon={"fa-light fa-calendar-days"}
              buttonLabel={`${t("Select Events")} (${
                selectedEvents?.length || 0
              })`}
              buttonFunction={() => {
                setDrawer({ isOpen: true, data: null });
              }}
              buttonClasses={"w-full md:w-auto"}
            />
            <ExportDropdown
              onExport={handleExport}
              isOpen={isExportOpen}
              onToggle={() => setIsExportOpen(!isExportOpen)}
              className="w-full"
            />
          </div>
        </div>

        {/* Orders Table */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["order", filters, search, selectedOrganization],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          // handleOrdering={(newData, oldData) =>
          //   handleOrdering({ newData, oldData })
          // }
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />

        {/* Modals */}
        <ManageOrderDrawer
          open={modalOpen?.isOpen}
          data={modalOpen?.data}
          close={() => setModalOpen({ isOpen: false, data: null })}
          getResponse={pageManageResponse}
        />
      </div>
    </div>
  );
};

export default OrderList;
